import { AudioSessionMode, CapacitorAudioRecorder } from "@capgo/capacitor-audio-recorder";
import type {
  LegacyGroupInfo,
  TakeErrorInfo,
  TakeFiles,
  TakeManifest,
  TakeMeta,
  TakeState,
  TakeStopResult,
} from "../captures/take-types";

// Réexportée ici pour ne casser aucun appelant existant : la fonction elle-même
// vit dans `take-meta.ts` (aucune dépendance à Capacitor, spec §5.2.1).
export { parseTakeMeta } from "../captures/take-meta";

/** Méthodes « prise » du plugin patché (spec §5.1). Découple la voie app du patch. */
export interface TakeRecorder {
  startTake(o: {
    takeId: string;
    /** Absent pour un avis vocal (C30) : aucune capture n'est prévue. */
    captureId?: string;
    meta: TakeMeta;
    sampleRate: number;
    bitRate: number;
  }): Promise<void>;
  stopTake(): Promise<TakeStopResult>;
  retryMerge(takeId: string): Promise<TakeStopResult>;
  listTakes(): Promise<{ takes: TakeManifest[]; legacyGroups: LegacyGroupInfo[] }>;
  getTakeSegments(takeId: string): Promise<TakeFiles>;
  markTake(o: {
    takeId: string;
    state?: TakeState;
    captureId?: string;
    uploadedSegment?: string;
    /** Segments que le serveur n'a pas (S-m3) : retirés des envoyés, renvoyés ensuite. */
    forgetUploadedSegments?: string[];
    /** Échec d'envoi ou d'appel vu par l'app (CA8) : `lastError` + journal natif. */
    error?: TakeErrorInfo;
  }): Promise<TakeManifest>;
  releaseTake(takeId: string): Promise<void>;
  trashTake(takeId: string): Promise<{ trashId: string; durationMs: number }>;
  adoptOrphans(groupId: string): Promise<TakeManifest>;
  resumeTake(takeId: string): Promise<void>;
}

// Les méthodes de prise sont déclarées dans `definitions.d.ts` du plugin patché.
// On les appelle quand même par leur nom : un build natif antérieur au patch ne
// les a pas (rejet explicite ci-dessous), et les types de `take-types.ts`
// restent ceux de l'app.
type Raw = Record<string, (o?: unknown) => Promise<unknown>>;

export function createTakeRecorder(plugin: unknown = CapacitorAudioRecorder): TakeRecorder {
  const p = plugin as Raw;
  const call = <T>(name: string, o?: unknown): Promise<T> => {
    const fn = p[name];
    // Build natif antérieur au patch des prises : échec explicite, jamais muet.
    if (!fn) return Promise.reject(new Error(`CapacitorAudioRecorder.${name} indisponible`));
    return fn(o) as Promise<T>;
  };
  return {
    startTake: ({ takeId, captureId, meta, sampleRate, bitRate }) =>
      call<void>("startRecording", {
        takeId,
        captureId,
        takeMeta: JSON.stringify(meta),
        audioSessionMode: AudioSessionMode.SpokenAudio,
        sampleRate,
        bitRate,
      }),
    stopTake: () => call<TakeStopResult>("stopRecording"),
    retryMerge: (takeId) => call<TakeStopResult>("retryMerge", { takeId }),
    listTakes: () => call("listTakes"),
    getTakeSegments: (takeId) => call<TakeFiles>("getTakeSegments", { takeId }),
    markTake: async (o) => (await call<{ take: TakeManifest }>("markTake", o)).take,
    releaseTake: (takeId) => call<void>("releaseTake", { takeId }),
    trashTake: (takeId) => call("trashTake", { takeId }),
    adoptOrphans: async (groupId) =>
      (await call<{ take: TakeManifest }>("adoptOrphans", { groupId })).take,
    resumeTake: (takeId) => call<void>("resumeTake", { takeId }),
  };
}

// Résultat d'arrêt reconnu : identifiant de prise et durée attendue toujours ;
// `merged` porte en plus le fichier et ses mesures. Toute autre forme (charge
// utile d'avant les prises : `{ uri, duration }`) est ignorée.
export function isTakeStopResult(v: unknown): v is TakeStopResult {
  if (typeof v !== "object" || v === null) return false;
  const r = v as Record<string, unknown>;
  if (typeof r.takeId !== "string" || typeof r.expectedMs !== "number") return false;
  if (r.status === "merge_failed") return true;
  return (
    r.status === "merged" &&
    typeof r.uri === "string" &&
    typeof r.measuredMs === "number" &&
    Array.isArray(r.unreadable)
  );
}

type ListenerPlugin = {
  addListener?: (
    event: string,
    fn: (ev: unknown) => void,
  ) => Promise<{ remove: () => Promise<void> | void }>;
};

/**
 * Écoute les résultats d'arrêt émis par le natif (spec §5.1) : `recordingStopped`
 * (le résultat lui-même, à chaque arrêt) et `recordingInterrupted` (`{ takeId,
 * reason, result }`, chemins « préserver »). Un même arrêt peut arriver par
 * plusieurs voies (événement ET promesse de `stopTake`) : l'appelant dédoublonne
 * par `takeId`. Rend la fonction de désabonnement.
 */
export function listenTakeResults(
  listener: (r: TakeStopResult) => void,
  plugin: unknown = CapacitorAudioRecorder,
): () => void {
  const p = plugin as ListenerPlugin;
  const handles = [
    p.addListener?.("recordingStopped", (ev) => {
      if (isTakeStopResult(ev)) listener(ev);
    }),
    p.addListener?.("recordingInterrupted", (ev) => {
      const result = (ev as { result?: unknown } | null)?.result;
      if (isTakeStopResult(result)) listener(result);
    }),
  ];
  return () => {
    for (const h of handles) void h?.then((x) => x.remove());
  };
}
